import React from "react";
import {
    Box,
    Typography,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    alpha,
    useTheme,
} from "@mui/material";
import { TableRestaurant, TakeoutDining } from "@mui/icons-material";

const OrdersListTable = ({ orders = [], onSelectOrder }) => {
    const theme = useTheme();

    const getOrderStatusStyles = (status) => {
        const map = {
            pending: { color: "#d97706", bg: alpha("#f59e0b", 0.12) },
            confirmed: { color: "#FF6600", bg: alpha("#FF6600", 0.12) },
            preparing: { color: "#4f46e5", bg: alpha("#667eea", 0.12) },
            ready: { color: "#059669", bg: alpha("#10b981", 0.12) },
            served: { color: "#4b5563", bg: alpha("#6b7280", 0.12) },
            completed: { color: "#047857", bg: alpha("#10b981", 0.12) },
            cancelled: { color: "#b91c1c", bg: alpha("#ef4444", 0.12) },
        };
        return map[status] || map.pending;
    };

    const getOrderItemsSummary = (order) => {
        const items = order.OrderItems || [];
        if (!items.length) {
            return { primary: "No items", secondary: "" };
        }

        const itemNames = items.map((item) => item?.MenuItem?.name || "Item");
        const primaryNames = itemNames.slice(0, 2).join(", ");
        const remainingCount = Math.max(itemNames.length - 2, 0);

        return {
            primary: primaryNames,
            secondary: remainingCount > 0 ? `+${remainingCount} more` : "",
        };
    };

    return (
        <TableContainer
            sx={{
                border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                borderRadius: "12px",
                overflow: "hidden",
                bgcolor: alpha(theme.palette.common.white, 0.9),
                boxShadow: `0 6px 18px ${alpha(theme.palette.common.black, 0.05)}`,
            }}
        >
            <Table stickyHeader size="small" sx={{ minWidth: 760 }}>
                <TableHead>
                    <TableRow>
                        <TableCell
                            sx={{
                                fontWeight: 800,
                                fontSize: "0.75rem",
                                py: 1,
                                textTransform: "uppercase",
                                letterSpacing: "0.4px",
                                color: theme.palette.text.secondary,
                                bgcolor: alpha(theme.palette.primary.main, 0.06),
                            }}
                        >
                            Order #
                        </TableCell>
                        <TableCell
                            sx={{
                                fontWeight: 800,
                                fontSize: "0.75rem",
                                py: 1,
                                textTransform: "uppercase",
                                letterSpacing: "0.4px",
                                color: theme.palette.text.secondary,
                                bgcolor: alpha(theme.palette.primary.main, 0.06),
                            }}
                        >
                            Type
                        </TableCell>
                        <TableCell
                            sx={{
                                fontWeight: 800,
                                fontSize: "0.75rem",
                                py: 1,
                                textTransform: "uppercase",
                                letterSpacing: "0.4px",
                                color: theme.palette.text.secondary,
                                bgcolor: alpha(theme.palette.primary.main, 0.06),
                            }}
                        >
                            Table/Mode
                        </TableCell>
                        <TableCell
                            sx={{
                                fontWeight: 800,
                                fontSize: "0.75rem",
                                py: 1,
                                textTransform: "uppercase",
                                letterSpacing: "0.4px",
                                color: theme.palette.text.secondary,
                                bgcolor: alpha(theme.palette.primary.main, 0.06),
                                minWidth: 220,
                            }}
                        >
                            Items
                        </TableCell>
                        <TableCell
                            sx={{
                                fontWeight: 800,
                                fontSize: "0.75rem",
                                py: 1,
                                textTransform: "uppercase",
                                letterSpacing: "0.4px",
                                color: theme.palette.text.secondary,
                                bgcolor: alpha(theme.palette.primary.main, 0.06),
                            }}
                        >
                            Status
                        </TableCell>
                        <TableCell
                            sx={{
                                fontWeight: 800,
                                fontSize: "0.75rem",
                                py: 1,
                                textTransform: "uppercase",
                                letterSpacing: "0.4px",
                                color: theme.palette.text.secondary,
                                bgcolor: alpha(theme.palette.primary.main, 0.06),
                            }}
                        >
                            Amount
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {orders.map((order) => {
                        const isDineIn = order.orderType === "dine-in";
                        const displayNumber = order.orderNumber
                            ? order.orderNumber.split("-").pop()
                            : `${order.id?.toString().slice(-4) || "N/A"}`;
                        const itemCount = order.OrderItems?.length || order.items || 0;
                        const itemsSummary = getOrderItemsSummary(order);
                        const totalAmount = parseFloat(order.totalAmount || order.total || 0);
                        const tableInfo = isDineIn
                            ? order.Table?.tableNumber || order.table || "N/A"
                            : "Takeaway";
                        const statusStyles = getOrderStatusStyles(order.status);

                        return (
                            <TableRow
                                key={order.id}
                                hover
                                onClick={() => onSelectOrder && onSelectOrder(order)}
                                sx={{
                                    cursor: "pointer",
                                    "& td": {
                                        py: 1,
                                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                    },
                                    "&:nth-of-type(even)": {
                                        bgcolor: alpha(theme.palette.grey[50], 0.7),
                                    },
                                    "&:hover": {
                                        bgcolor: alpha(theme.palette.primary.main, 0.07),
                                    },
                                }}
                            >
                                <TableCell sx={{ fontWeight: 700, color: theme.palette.primary.main, fontSize: "0.8rem" }}>
                                    #{displayNumber}
                                </TableCell>
                                <TableCell sx={{ fontSize: "0.78rem" }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                                        {isDineIn ? (
                                            <TableRestaurant sx={{ fontSize: 15, color: "#10b981" }} />
                                        ) : (
                                            <TakeoutDining sx={{ fontSize: 15, color: "#f59e0b" }} />
                                        )}
                                        <Typography variant="caption" sx={{ fontSize: "0.75rem", fontWeight: 600 }}>
                                            {isDineIn ? "Dine-in" : "Takeaway"}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ fontSize: "0.78rem", fontWeight: 600 }}>{tableInfo}</TableCell>
                                <TableCell sx={{ fontSize: "0.78rem" }}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                display: "block",
                                                fontWeight: 700,
                                                fontSize: "0.74rem",
                                                color: theme.palette.text.primary,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                            title={itemsSummary.primary}
                                        >
                                            {itemsSummary.primary}
                                        </Typography>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.2 }}>
                                            <Chip
                                                size="small"
                                                label={`${itemCount} item${itemCount === 1 ? "" : "s"}`}
                                                sx={{
                                                    height: 18,
                                                    fontSize: "0.62rem",
                                                    fontWeight: 700,
                                                    bgcolor: alpha(theme.palette.info.main, 0.12),
                                                    color: theme.palette.info.dark,
                                                }}
                                            />
                                            {itemsSummary.secondary && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontSize: "0.66rem",
                                                        color: theme.palette.text.secondary,
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {itemsSummary.secondary}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={order.status || "pending"}
                                        size="small"
                                        sx={{
                                            height: 23,
                                            textTransform: "capitalize",
                                            fontWeight: 700,
                                            fontSize: "0.66rem",
                                            color: statusStyles.color,
                                            bgcolor: statusStyles.bg,
                                            border: `1px solid ${alpha(statusStyles.color, 0.28)}`,
                                            boxShadow: `inset 0 0 0 1px ${alpha(statusStyles.color, 0.05)}`,
                                        }}
                                    />
                                </TableCell>
                                <TableCell sx={{ fontSize: "0.8rem", fontWeight: 700 }}>
                                    Rs. {totalAmount.toFixed(2)}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default OrdersListTable;
